import { gql, NetworkStatus } from '@apollo/client';
import {
  Box,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Spinner,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import { z } from 'zod';
import BackToAllActionsButton from '~components/displays/BulkAction/BackToAllActionsButton';
import { WarningAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { components, selectOptionBaseSchema } from '~components/ui/Select';
import { ConfigurationStatus, DisplayPlatform } from '~graphql/__generated__/types';
import { useAnalyticsReporter } from '~utils/analytics';
import { ensure } from '~utils/types';
import { BulkActionModal_DisplayFragment } from './__generated__/BulkActionModal.graphql';
import {
  useAssignConfigurationMutation,
  useCustomerConifgurationsListQuery,
} from './__generated__/BulkAssignConfiguration.graphql';
import { BulkActionComponent } from './BulkActionModal';

const configurationOption = selectOptionBaseSchema;
type ConfigurationOption = z.TypeOf<typeof configurationOption>;

const schema = z.object({
  displayIds: z.array(z.string()),
  configuration: configurationOption.nullish(),
});
type FormValues = z.TypeOf<typeof schema>;

function checkPlatform(display: BulkActionModal_DisplayFragment, platform: DisplayPlatform) {
  return (
    display.platform.name === platform.name &&
    display.platform.version === platform.version &&
    display.platform.type === platform.type
  );
}
interface BulkAssignConfigurationProps {
  displays: BulkActionModal_DisplayFragment[];
  onCancel: () => void;
  onBack?: () => void | Promise<void>;
  onSuccess: () => void;
  hideBackButton?: boolean;
}

export const BulkAssignConfigurationInner = ({
  displays,
  onCancel,
  onBack,
  onSuccess,
  hideBackButton,
}: BulkAssignConfigurationProps) => {
  const { customer } = useParams();
  const toast = useToast();
  const analytics = useAnalyticsReporter();

  const [hasSelection, setHasSelection] = useState(false);

  const allDisplaysHaveSamePlatform = displays.every((display) =>
    checkPlatform(display, displays[0].platform),
  );
  const commonPlatform = allDisplaysHaveSamePlatform ? displays[0].platform.name : null;

  const { data, loading, refetch, networkStatus } = useCustomerConifgurationsListQuery({
    variables: {
      customerHandle: ensure(customer),
      pageNumber: 1,
      // pageSize: 50, commented to allow all configs to be visible for a given platform
      filter: {
        status: [ConfigurationStatus.Published],
      },
    },
  });
  const [AssignConfigurationMutation] = useAssignConfigurationMutation();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const options = useMemo<ConfigurationOption[]>(() => {
    if (loading) return [];
    return (data?.customer?.configurations ?? [])
      .filter((p) => {
        if (!commonPlatform || !p.platform) return false;
        return p.platform.toString() === commonPlatform.toString();
      })
      .map<ConfigurationOption>((p) => ({
        label: p.name,
        value: p.id,
      }));
  }, [data, commonPlatform, loading]);

  const isFetchingData = loading || networkStatus === NetworkStatus.refetch;
  const noCompatibleConfigs = !loading && allDisplaysHaveSamePlatform && options.length === 0;

  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      configuration: null,
      displayIds: displays.map((d) => d.id),
    },
    resolver: zodResolver(schema),
  });
  const handleBulkAssignConfiguration = useCallback(
    async ({ displayIds, configuration }: FormValues) => {
      try {
        if (configuration?.value) {
          await AssignConfigurationMutation({
            variables: {
              input: {
                displayIds,
                id: configuration?.value,
              },
            },
            refetchQueries: ['CustomerDisplaysIndexPage'],
            awaitRefetchQueries: true,
          });
        }
        if (displayIds.length === 1) {
          analytics.track('configurationUpdate');
        } else {
          analytics.track('displayBulkActionComplete', {
            action: 'updateConfiguration',
            displayCount: displayIds.length,
          });
        }

        await onSuccess();
        toast({
          status: 'success',
          title: t('configAssignSuccess'),
        });
      } catch (err) {
        toast({
          status: 'error',
          title: t('configAssignError'),
        });
      }
    },
    [analytics, onSuccess, t, toast, AssignConfigurationMutation],
  );

  return (
    <>
      <ModalContent>
        <ModalHeader>{t('assignConfiguration')}</ModalHeader>
        <ModalCloseButton onClick={onCancel} isDisabled={isSubmitting} />
        <form onSubmit={handleSubmit(handleBulkAssignConfiguration)}>
          <ModalBody>
            <Stack spacing="4">
              {isFetchingData && (
                <Center>
                  {' '}
                  <Spinner size="sm" color="blue.500" />{' '}
                </Center>
              )}

              {!loading && allDisplaysHaveSamePlatform && !noCompatibleConfigs && (
                <FormControl isInvalid={Boolean(errors.configuration)}>
                  <FormLabel>{t('selectConfiguration')}</FormLabel>
                  <Controller
                    name="configuration"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        options={options}
                        components={components}
                        onChange={(val) => {
                          field.onChange(val);
                          setHasSelection(Boolean(val));
                        }}
                        isLoading={isSubmitting}
                        isMulti={false}
                        escapeClearsValue={false}
                        isClearable={true}
                        isSearchable={true}
                        menuPlacement="auto"
                      />
                    )}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="configuration"
                    render={({ message }) => <FormErrorMessage>{message}</FormErrorMessage>}
                  />
                </FormControl>
              )}

              {!loading && !allDisplaysHaveSamePlatform && (
                <WarningAlert>{t('multiDisplayModelsSelectedAlert')}</WarningAlert>
              )}
              {noCompatibleConfigs && (
                <WarningAlert>{t('noCompatibleConfigurationsFound')}</WarningAlert>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <HStack flex="1" alignItems="center">
              {!hideBackButton && (
                <BackToAllActionsButton onBack={onBack} isDisabled={isSubmitting} />
              )}{' '}
              <Box flex="1" display="flex" justifyContent="flex-end" alignItems="center">
                <Button
                  variant="ghost"
                  colorScheme="blue"
                  onClick={onCancel}
                  isDisabled={isSubmitting}
                >
                  {t('cancel')}
                </Button>
                <Button
                  marginLeft="3"
                  type="submit"
                  variant="solid"
                  colorScheme="blue"
                  isDisabled={
                    isSubmitting ||
                    !allDisplaysHaveSamePlatform ||
                    noCompatibleConfigs ||
                    !hasSelection
                  }
                  isLoading={isSubmitting}
                >
                  {t('apply')}
                </Button>
              </Box>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </>
  );
};

BulkAssignConfigurationInner.graphql = {
  mutations: {
    BulkAssignConfigurationInner: gql`
      mutation assignConfiguration($input: DisplayBulkAssignConfigurationsInput!) {
        assignConfiguration(input: $input)
      }
    `,
  },
  queries: {
    CustomerConifgurationsList: gql`
      query CustomerConifgurationsList(
        $customerHandle: String!
        $pageNumber: Int!
        $filter: ConfigurationFilters
      ) {
        customer: customerByHandle(handle: $customerHandle) {
          id
          configurations(pageNumber: $pageNumber, filter: $filter) {
            id
            name
            state
            platform
          }
        }
      }
    `,
  },
};

export const BulkAssignConfiguration: BulkActionComponent = (props) => {
  return (
    <BulkAssignConfigurationInner
      displays={props.displays}
      onCancel={props.onCancel}
      onBack={props.onBack}
      onSuccess={props.onSuccess}
    />
  );
};
